/**
 * batch-59 voertuig-afspraken (FO-435 §3 stap 4) - DE pure actie-status van een
 * voertuig.
 *
 * Het probleem dat dit oplost: het voertuigdossier wist wél wanneer de APK
 * verloopt, maar niet of er iets aan gedaan wordt. Jeroens APK vervalt over 80
 * dagen MET een geplande afspraak (groen); Jelles APK vervalt over 20 dagen
 * ZONDER afspraak (rood). Zonder afspraak-begrip zagen die twee er identiek uit.
 *
 * De functie is bewust PUUR (geen Prisma, geen fetch): de API rekent 'm
 * server-side uit voor de lijst, de KPI-teller en het detail, de UI toont het
 * resultaat. Nooit client-side herberekenen - dan lopen scherm en teller uiteen.
 *
 * Bewaakte soorten:
 *   - APK          - `Voertuig.apkVervaldatum`
 *   - VERZEKERING  - `Voertuig.verzekeringVervaldatum`
 *   - TERUGROEP    - openstaande RDW-recall die INTERN nog open is (FO-436)
 *   - ONDERHOUD    - batch-62 (FO-465 §8): de reminder "volgende actie"
 *                    (kleine/grote beurt, banden) - datum EN km-drempel
 *
 * De koppeling soort -> afspraak-soort staat in `AFSPRAAK_DEKKING` (één plek,
 * één regel-wijziging als het beleid verandert).
 */

import { dagenTotVerloop } from './voertuig-utils'

/** De bewaakte soorten (volgorde = weergavevolgorde). */
export const VOERTUIG_ACTIE_SOORTEN = ['APK', 'VERZEKERING', 'TERUGROEP', 'ONDERHOUD'] as const
export type VoertuigActieSoort = (typeof VOERTUIG_ACTIE_SOORTEN)[number]

/** Afspraak-soorten zoals in het schema (`VoertuigAfspraakSoort`). */
export type VoertuigAfspraakSoortWaarde = 'ONDERHOUD' | 'SCHADE' | 'APK' | 'REPARATIE'

/** Afspraak-statussen zoals in het schema (`VoertuigAfspraakStatus`). */
export type VoertuigAfspraakStatusWaarde =
  | 'TE_PLANNEN'
  | 'GEPLAND'
  | 'UITGEVOERD'
  | 'GEANNULEERD'
  | 'NIET_VERSCHENEN'

/**
 * Welke afspraak-soort dekt welk verval-signaal?
 *
 *   APK         -> een APK-afspraak (de keuring zelf).
 *   TERUGROEP   -> een ONDERHOUD-afspraak; dat is exact wat FO-436 §6 voorschrijft
 *                  ("vanuit de melding Afspraak plannen -> afspraak type ONDERHOUD
 *                  met recall-referentie").
 *   VERZEKERING -> een ONDERHOUD-afspraak. De verlenging zelf is administratief,
 *                  maar de praktijk is dat het samenloopt met de garage-/dealer-
 *                  ronde; staat die gepland vóór de vervaldatum, dan loopt het.
 *                  Een SCHADE-afspraak dekt nooit een verval-signaal.
 */
export const AFSPRAAK_DEKKING: Record<VoertuigActieSoort, readonly VoertuigAfspraakSoortWaarde[]> = {
  APK: ['APK'],
  VERZEKERING: ['ONDERHOUD'],
  TERUGROEP: ['ONDERHOUD'],
  // batch-62 (FO-465 §8) - een beurt/bandenwissel wordt gedekt door een
  // ONDERHOUD-afspraak (zelfde regel als `afspraakSoortVoorReminder`).
  ONDERHOUD: ['ONDERHOUD'],
}

/**
 * Niveaus, oplopend in urgentie. `GEEN` = niets aan de hand (buiten de horizon).
 */
export type VoertuigActieNiveau = 'GEEN' | 'UITGEVOERD' | 'GEPLAND' | 'ACTIE_VEREIST'

const NIVEAU_RANG: Record<VoertuigActieNiveau, number> = {
  GEEN: 0,
  UITGEVOERD: 1,
  GEPLAND: 2,
  ACTIE_VEREIST: 3,
}

export const VOERTUIG_ACTIE_NIVEAU_LABELS: Record<VoertuigActieNiveau, string> = {
  GEEN: 'Geen actie',
  UITGEVOERD: 'Uitgevoerd',
  GEPLAND: 'Gepland',
  ACTIE_VEREIST: 'Actie vereist',
}

export const VOERTUIG_ACTIE_SOORT_LABELS: Record<VoertuigActieSoort, string> = {
  APK: 'APK',
  VERZEKERING: 'Verzekering',
  TERUGROEP: 'Terugroepactie',
  ONDERHOUD: 'Onderhoud',
}

/** Minimale afspraak-vorm die de status-berekening nodig heeft. */
export interface ActieStatusAfspraak {
  id: string
  soort: VoertuigAfspraakSoortWaarde
  status: VoertuigAfspraakStatusWaarde
  /** ISO (yyyy-mm-dd of volledige ISO-string) of null bij TE_PLANNEN. */
  afspraakDatum: string | null
  /** ISO of null; gevuld zodra de afspraak is afgerond. */
  uitgevoerdOp: string | null
}

/** Minimale voertuig-vorm (alleen de bewaakte vervaldata + de recall-stand). */
export interface ActieStatusVoertuig {
  /** ISO of null. */
  apkVervaldatum: string | null
  /** ISO of null. */
  verzekeringVervaldatum: string | null
  /**
   * Aantal recalls dat RDW openstaand meldt EN dat intern nog open is
   * (interneStatus OPEN of INGEPLAND). Zie FO-436: intern UITGEVOERD/NVT telt
   * NIET meer als werkvoorraad, ook al blijft RDW op "O" staan.
   */
  openTerugroepactiesIntern: number
  /**
   * batch-62 garage-voertuig (FO-465 §8) - de urgentste reminder "volgende
   * actie" (kleine/grote beurt, banden; APK loopt via `apkVervaldatum`).
   * Weglaten of null = geen reminder (bestaand gedrag).
   */
  volgendOnderhoud?: {
    /** ISO-dag van de eerste drempel (datum of geschatte km-datum). */
    vervaldatum: string | null
    /** Dagen vóór de vervaldatum waarop de actie "vereist" wordt. */
    drempelDagen: number
    /** True = de km-drempel (min. de km-marge) is al bereikt. */
    kmDrempelBereikt: boolean
  } | null
}

export interface VoertuigActieStatusOpties {
  /** Peildatum (default: nu). */
  vandaag?: Date
  /** Horizon per soort in dagen (APK default 30, instelbaar - FO-435 §10 A4). */
  drempelDagen?: Partial<Record<VoertuigActieSoort, number>>
  /**
   * Hoe lang een afgeronde afspraak nog als "Uitgevoerd" getoond wordt terwijl
   * de vervaldatum nog niet is bijgewerkt (default 60 dagen). Daarna valt het
   * signaal terug op ACTIE_VEREIST - anders zou een vergeten APK-bevestiging
   * eeuwig groen blijven.
   */
  uitgevoerdVensterDagen?: number
}

export const VOERTUIG_ACTIE_DREMPEL_DAGEN: Record<VoertuigActieSoort, number> = {
  APK: 30,
  VERZEKERING: 30,
  // Een recall heeft geen vervaldatum: zolang hij intern open staat is er actie.
  TERUGROEP: 0,
  ONDERHOUD: 30,
}

const UITGEVOERD_VENSTER_DAGEN = 60

export interface VoertuigActieStatusItem {
  soort: VoertuigActieSoort
  niveau: VoertuigActieNiveau
  /** ISO-datum van het vervalmoment; null bij TERUGROEP (geen datum). */
  vervaldatum: string | null
  /** Dagen tot verval (negatief = verlopen); null als er geen datum is. */
  dagenTotVerloop: number | null
  /** De dekkende afspraak (indien gevonden). */
  afspraakId: string | null
  /** ISO-datum van de dekkende afspraak; null bij TE_PLANNEN zonder datum. */
  afspraakDatum: string | null
  /**
   * True zodra er wél een afspraak is maar nog GEEN datum (status TE_PLANNEN).
   * Dat blijft ACTIE_VEREIST - "op te plannen" is geen planning - maar de UI
   * kan het onderscheid tonen ("Actie vereist - staat op de lijst").
   */
  wachtOpDatum: boolean
  /**
   * Waarom een BESTAANDE geplande afspraak dit signaal NIET dekt:
   *   VERSTREKEN      - de afspraakdatum ligt in het verleden en de afspraak is
   *                     nooit afgerond (hij is dus niet nagekomen);
   *   NA_VERVALDATUM  - de afspraak staat ná het vervalmoment, dus het voertuig
   *                     rijdt er een periode onverzekerd/zonder APK tussenin.
   * In beide gevallen is het niveau ACTIE_VEREIST: groen tonen zou het hele
   * monitoring-doel omkeren.
   */
  afspraakProbleem: 'VERSTREKEN' | 'NA_VERVALDATUM' | null
}

export interface VoertuigActieStatusResultaat {
  /** Het urgentste niveau over alle soorten. */
  niveau: VoertuigActieNiveau
  /** True zodra minstens één soort ACTIE_VEREIST is. */
  actieVereist: boolean
  /** Alle soorten met een niveau != GEEN, urgentste eerst. */
  items: VoertuigActieStatusItem[]
}

/** Kalenderdag-ISO (yyyy-mm-dd) van een ISO-string; null-tolerant. */
function isoDag(waarde: string | null): string | null {
  if (!waarde) return null
  return waarde.length > 10 ? waarde.slice(0, 10) : waarde
}

function parseDatum(waarde: string | null): Date | null {
  const dag = isoDag(waarde)
  if (!dag) return null
  const d = new Date(`${dag}T00:00:00.000Z`)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Afspraken die deze soort kunnen dekken, nieuwste geplande datum eerst. */
function dekkendeAfspraken(
  soort: VoertuigActieSoort,
  afspraken: readonly ActieStatusAfspraak[],
): ActieStatusAfspraak[] {
  const soorten = AFSPRAAK_DEKKING[soort]
  return afspraken.filter((a) => soorten.includes(a.soort))
}

/**
 * De actie-status van één voertuig. Puur: geen I/O, geen `new Date()` buiten de
 * peildatum, zodat de test elk grensgeval kan vastzetten.
 */
export function voertuigActieStatus(
  voertuig: ActieStatusVoertuig,
  afspraken: readonly ActieStatusAfspraak[],
  opties: VoertuigActieStatusOpties = {},
): VoertuigActieStatusResultaat {
  const vandaag = opties.vandaag ?? new Date()
  const uitgevoerdVenster = opties.uitgevoerdVensterDagen ?? UITGEVOERD_VENSTER_DAGEN
  const items: VoertuigActieStatusItem[] = []

  for (const soort of VOERTUIG_ACTIE_SOORTEN) {
    const drempel = opties.drempelDagen?.[soort] ?? VOERTUIG_ACTIE_DREMPEL_DAGEN[soort]

    // 1. Is er iets aan de hand? (binnen de horizon / intern-open recall)
    let vervaldatum: string | null = null
    let dagen: number | null = null
    let relevant = false
    if (soort === 'TERUGROEP') {
      relevant = voertuig.openTerugroepactiesIntern > 0
    } else if (soort === 'ONDERHOUD') {
      // batch-62 (FO-465 §8) - de reminder heeft een eigen drempel (per soort
      // beheerbaar) en kan ook via de km-drempel al "bereikt" zijn.
      const vo = voertuig.volgendOnderhoud ?? null
      vervaldatum = isoDag(vo?.vervaldatum ?? null)
      const datum = parseDatum(vervaldatum)
      dagen = datum ? dagenTotVerloop(datum, vandaag) : null
      const onderhoudDrempel = vo?.drempelDagen ?? drempel
      relevant =
        vo !== null && (vo.kmDrempelBereikt || (dagen !== null && dagen <= onderhoudDrempel))
    } else {
      vervaldatum = isoDag(
        soort === 'APK' ? voertuig.apkVervaldatum : voertuig.verzekeringVervaldatum,
      )
      const datum = parseDatum(vervaldatum)
      dagen = datum ? dagenTotVerloop(datum, vandaag) : null
      relevant = dagen !== null && dagen <= drempel
    }
    if (!relevant) continue

    // 2. Dekt een afspraak dit signaal?
    //
    // Een GEPLANDE afspraak dekt alleen als hij er ook echt nog op tijd bij is:
    // niet verstreken (dan is hij niet nagekomen) en niet ná het vervalmoment
    // (dan rijdt het voertuig er tussenin zonder geldige APK/verzekering).
    const vandaagIso = isoDag(vandaag.toISOString())!
    const kandidaten = dekkendeAfspraken(soort, afspraken)
    const gepland = kandidaten
      .filter((a) => a.status === 'GEPLAND' && a.afspraakDatum !== null)
      .sort((a, b) => (isoDag(a.afspraakDatum) ?? '').localeCompare(isoDag(b.afspraakDatum) ?? ''))
    const open = gepland.filter((a) => {
      const dag = isoDag(a.afspraakDatum)!
      if (dag < vandaagIso) return false
      return vervaldatum === null || dag <= vervaldatum
    })
    const tePlannen = kandidaten.filter((a) => a.status === 'TE_PLANNEN')
    const uitgevoerd = kandidaten
      .filter((a) => a.status === 'UITGEVOERD' && a.uitgevoerdOp !== null)
      .sort((a, b) => (isoDag(b.uitgevoerdOp) ?? '').localeCompare(isoDag(a.uitgevoerdOp) ?? ''))

    if (open.length > 0) {
      const a = open[0]!
      items.push({
        soort,
        niveau: 'GEPLAND',
        vervaldatum,
        dagenTotVerloop: dagen,
        afspraakId: a.id,
        afspraakDatum: isoDag(a.afspraakDatum),
        wachtOpDatum: false,
        afspraakProbleem: null,
      })
      continue
    }

    const recentUitgevoerd = uitgevoerd.find((a) => {
      const d = parseDatum(a.uitgevoerdOp)
      if (!d) return false
      const dagenTot = dagenTotVerloop(d, vandaag)
      if (dagenTot === null) return false
      const dagenGeleden = -dagenTot
      return dagenGeleden >= 0 && dagenGeleden <= uitgevoerdVenster
    })
    if (recentUitgevoerd) {
      items.push({
        soort,
        niveau: 'UITGEVOERD',
        vervaldatum,
        dagenTotVerloop: dagen,
        afspraakId: recentUitgevoerd.id,
        afspraakDatum: isoDag(recentUitgevoerd.afspraakDatum),
        wachtOpDatum: false,
        afspraakProbleem: null,
      })
      continue
    }

    // Er is wél een geplande afspraak, maar hij dekt het signaal niet. Vertel
    // waarom - "Actie vereist" zonder uitleg is precies zo nutteloos als een
    // groen vinkje op een gemiste afspraak.
    const probleemAfspraak = gepland[0] ?? null
    const probleem: 'VERSTREKEN' | 'NA_VERVALDATUM' | null = probleemAfspraak
      ? (isoDag(probleemAfspraak.afspraakDatum)! < vandaagIso ? 'VERSTREKEN' : 'NA_VERVALDATUM')
      : null

    items.push({
      soort,
      niveau: 'ACTIE_VEREIST',
      vervaldatum,
      dagenTotVerloop: dagen,
      afspraakId: probleemAfspraak?.id ?? tePlannen[0]?.id ?? null,
      afspraakDatum: probleem ? isoDag(probleemAfspraak!.afspraakDatum) : null,
      wachtOpDatum: probleem === null && tePlannen.length > 0,
      afspraakProbleem: probleem,
    })
  }

  items.sort((a, b) => NIVEAU_RANG[b.niveau] - NIVEAU_RANG[a.niveau])
  const niveau = items[0]?.niveau ?? 'GEEN'
  return {
    niveau,
    actieVereist: items.some((i) => i.niveau === 'ACTIE_VEREIST'),
    items,
  }
}

/**
 * Korte labeltekst voor één item ("Actie vereist", "Gepland 12-04-2026",
 * "Uitgevoerd"). De datum staat in NL-notatie (dd-MM-yyyy), conform CLAUDE.md.
 */
export function voertuigActieLabel(item: VoertuigActieStatusItem): string {
  if (item.niveau === 'GEPLAND' && item.afspraakDatum) {
    return `Gepland ${nlDatumUitIso(item.afspraakDatum)}`
  }
  if (item.niveau === 'ACTIE_VEREIST') {
    if (item.afspraakProbleem === 'VERSTREKEN' && item.afspraakDatum) {
      return `Actie vereist - afspraak van ${nlDatumUitIso(item.afspraakDatum)} niet afgerond`
    }
    if (item.afspraakProbleem === 'NA_VERVALDATUM' && item.afspraakDatum) {
      return `Actie vereist - afspraak pas op ${nlDatumUitIso(item.afspraakDatum)}`
    }
    if (item.wachtOpDatum) return 'Actie vereist - nog te plannen'
  }
  return VOERTUIG_ACTIE_NIVEAU_LABELS[item.niveau]
}

/** yyyy-mm-dd -> dd-MM-yyyy (geen Date-constructie, dus tijdzone-neutraal). */
export function nlDatumUitIso(iso: string): string {
  const dag = isoDag(iso)
  if (!dag || dag.length !== 10) return iso
  return `${dag.slice(8, 10)}-${dag.slice(5, 7)}-${dag.slice(0, 4)}`
}
